import { Button, ButtonProps } from '@chakra-ui/react';
import { Link } from 'gatsby-plugin-jaen';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { usePageLocale } from '../contexts/locale';
import { defaultLocale, locales } from '../locales/messages';

/**
 * The language's name in that language ("Deutsch", "English"), from Intl
 * rather than a hardcoded table, so adding a locale to the site needs no edit
 * here. Falls back to the bare code if the runtime has no data for it.
 */
const autonym = (locale: string): string => {
  try {
    const names = new Intl.DisplayNames([locale], { type: 'language' });
    const name = names.of(locale);

    if (!name || name === locale) return locale.toUpperCase();

    return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  } catch {
    return locale.toUpperCase();
  }
};

export interface LanguageSwitcherProps extends ButtonProps {}

/**
 * A toggle, not a menu.
 *
 * The site has exactly two locales, and a dropdown that opens to reveal one
 * choice is a worse control than a button that simply is that choice. netsnek.com
 * has five and needs the menu; this does not.
 *
 * The target path comes from the page context's `translations`, which
 * gatsby-source-jaen writes for every localized page. A page with no
 * translation entry falls back to the other locale's root rather than to a
 * path that does not exist: `/en/` for a prefixed locale, `/` for the default
 * one, which is served unprefixed.
 *
 * It renders a real anchor rather than calling navigate(), so the alternate
 * language is a link a crawler can follow and a reader can open in a new tab.
 */
const LanguageSwitcher: FC<LanguageSwitcherProps> = props => {
  const { locale: currentLocale, translations } = usePageLocale();
  const intl = useIntl();

  const currentBase = currentLocale.split(/[-_]/)[0]?.toLowerCase();
  const target = locales.find(locale => locale !== currentBase) ?? defaultLocale;

  const translation = translations.find(
    entry => entry.locale.split(/[-_]/)[0]?.toLowerCase() === target
  );
  const href =
    translation?.path ?? (target === defaultLocale ? '/' : `/${target}/`);

  const label = intl.formatMessage({
    id: 'LanguageSwitcherLabel',
    defaultMessage: 'Sprache wählen'
  });

  return (
    <Button
      asChild
      variant="ghost"
      size="sm"
      minH="10"
      px={3}
      fontWeight="bold"
      // The button reads as the language it switches TO, so the accessible
      // name has to say that it is a switch and not a statement of where the
      // reader already is.
      aria-label={`${label}: ${autonym(target)}`}
      {...props}
    >
      <Link to={href} lang={target} hrefLang={target}>
        {target.toUpperCase()}
      </Link>
    </Button>
  );
};

export default LanguageSwitcher;
